/* eslint no-inline-comments: 0 */

import { notify } from 'notify';
import { useMainStore } from 'store';
import { nextTick } from 'vue';
import { createRouter, createWebHistory } from 'vue-router';

import authors_routes from '../authors/_routes.js';
import automations_routes from '../automations/_routes.js';
import blocked_domains_routes from '../blocked_domains/_routes.js';
import broadcasts_routes from '../broadcasts/_routes.js';
import cms_routes from '../cms/_routes.js';
import consents_routes from '../consents/_routes.js';
import genai_routes from '../genai/_routes.js';
import help_center_routes from '../help_center/_routes.js';
import industry_attributes_routes from '../industry_attributes/_routes.js';
import job_functions_routes from '../job_functions/_routes.js';
import mailing_list_routes from '../mailing_lists/_routes.js';
import media_routes from '../media/_routes.js';
import { members_routes } from '../members/_routes.js';
import { accounts_logged_out_routes, get_accounts_logged_in_routes, get_members_logged_in_routes, members_logged_out_routes } from '../members_portal/_routes.js';
import migrations_routes from '../migrations/_routes.js';
import network_rules_routes from '../network_rules/_routes.js';
import oauth2_routes from '../oauth2/_routes.js';
import organizations_routes from '../organizations/_routes.js';
import professional_services_routes from '../professional_services/_routes.js';
import projects_routes from '../projects/_routes.js';
import seniorities_routes from '../seniorities/_routes.js';
import sensors_routes from '../sensors/_routes.js';
import stringer_routes from '../stringer/_routes.js';
import subscriptions_routes from '../subscriptions/_routes.js';
import tags_routes from '../tags/_routes.js';
import url_shortener_routes from '../url_shortener/_routes.js';
import { users_logged_in_routes, users_logged_out_routes, users_routes } from '../users/_routes.js';

const Container = () => import( /* webpackChunkName: "group-admin" */ `../container.vue` );

export let router = {};

export function create_router( response ) {
	// Central sign-in cutover (#2361). central_sign_in_url is only in the
	// bootstrap on a brand portal whose brand is listed in
	// CENTRAL_SIGNIN_BRANDS; everywhere else it is absent and none of this
	// does anything. The server already sends a signed-out page load to the
	// central host (members_portal/central_signin.go); this covers the
	// in-app links of a signed-out page (a "Log in" link on the reset page,
	// say), which never reach the server.
	let members_signed_out = false;

	function central_sign_in_active() {
		return response.site.portal === `members` && Boolean( response.central_sign_in_url ) && members_signed_out;
	}

	function go_to_central_sign_in( page, return_to ) {
		const params = new URLSearchParams();

		params.set( `page`, page );

		if( return_to ) {
			params.set( `return_to`, return_to );
		}

		location.assign( `${response.central_sign_in_url}?${params.toString()}` );
	}

	const route = {
		path: `/`,
		component: Container,
		children: [
		]
	};

	switch( response.site.portal ) {
	case `admin`:
		if( response.user?.uuid ) {
			route.children.push( ...users_logged_in_routes );
			route.children.push( authors_routes );
			route.children.push( automations_routes );
			route.children.push( blocked_domains_routes );
			route.children.push( broadcasts_routes );
			route.children.push( cms_routes );
			route.children.push( consents_routes );
			route.children.push( help_center_routes );
			route.children.push( industry_attributes_routes );
			route.children.push( job_functions_routes );
			route.children.push( mailing_list_routes );
			route.children.push( media_routes );
			route.children.push( members_routes );
			route.children.push( genai_routes );
			route.children.push( migrations_routes );
			route.children.push( network_rules_routes );
			route.children.push( oauth2_routes );
			route.children.push( organizations_routes );
			route.children.push( professional_services_routes );
			route.children.push( projects_routes );
			route.children.push( seniorities_routes );
			route.children.push( sensors_routes );
			route.children.push( stringer_routes );
			route.children.push( subscriptions_routes );
			route.children.push( tags_routes );
			route.children.push( url_shortener_routes );
			route.children.push( users_routes );
		} else {
			route.children.push( ...users_logged_out_routes );
		}
		break;
	case `members`:
		// prompt=login (mono#1853): the authorize redirect sets this on the
		// OUTER URL (oauth2/members_api.go's redirectToLoginForm), never
		// inside the pending redirect_to, specifically so it is readable
		// here before the SPA has picked a route table. Without it, a
		// browser with a live session always gets get_members_logged_in_routes
		// below and lands on its profile instead of the login form the app
		// asked for -- session and route table are otherwise the same
		// question, and this is the one place they have to be asked
		// separately.
		if( response.member?.uuid && !response?.is_partial && new URLSearchParams( location.search ).get( `prompt` ) !== `login` ) {
			route.children.push( ...get_members_logged_in_routes( response ) );
		} else {
			route.children.push( ...members_logged_out_routes );

			members_signed_out = true;
		}

		break;
	case `accounts`:
		// The central sign-in host (#2358). Same prompt=login rule as the
		// members case above, for the same reason: the authorize redirect
		// can force the login form over a live session.
		if( response.member?.uuid && !response?.is_partial && new URLSearchParams( location.search ).get( `prompt` ) !== `login` ) {
			route.children.push( ...get_accounts_logged_in_routes( response ) );
		} else {
			route.children.push( ...accounts_logged_out_routes );
		}

		break;
	}

	route.children.push(
		{
			path: `/:catchAll(.*)`,
			name: `redirect`,
			beforeEnter( to, from, next ) {
				// The accounts host keeps where the visitor was going, the way
				// the admin does: a signed-out member following an app's
				// consent link (/consent?redirect_to=...) signs in first and
				// then lands back on it (#2358).
				//
				// Absolute, because the magic-link email only carries a
				// redirect_to whose host is one of our sites.
				if( response.site.portal === `accounts` ) {
					next( {
						name: `dashboard`,
						query: {
							redirect_to: location.origin + to.fullPath
						}
					} );
					return;
				}

				// A signed-out visitor on a cut-over brand portal signs in
				// centrally and comes back to the page they asked for.
				if( central_sign_in_active() ) {
					go_to_central_sign_in( `login`, to.fullPath );
					next( false );
					return;
				}

				if( response.site.portal === `admin` ) {
					next( {
						name: `dashboard`,
						query: {
							redirect_to: to.query.redirect_to ?? to.fullPath
						}
					} );
					return;
				}

				next( {
					name: `dashboard`,
					query: {}
				} );
			}
		}
	);

	router = createRouter(
		{
			history: createWebHistory(),
			scrollBehavior( to, from ) {
				if( to.name === from.name ) {
					return {
						top: 0
					};
				}

				return null;
			},
			routes: [
				route
			]
		}
	);

	// This has to agree with the server, exactly, in both directions. Say no
	// where the server says yes and a member browses a portal every request of
	// which will 401; say yes where the server says no and they are pinned to
	// the security page with nothing left to set up.
	//
	// So the two portals answer the last question differently, because their
	// servers do. members.VerifySessionWithMFA now accepts a verified passkey
	// as well as an OTP app (mfa.SatisfyingMethods); users.VerifySessionWithMFA,
	// the admin path, still accepts only an OTP app, and is deliberately left
	// alone. A member who had enrolled a passkey and no OTP app was bounced to
	// the security screen on every route by the old single test -- the screen
	// already showing them the passkey they were being told to go and create.
	function needs_mfa() {
		// The accounts host (#2358) is a members host for MFA: same server
		// check (members.VerifySessionWithMFA), same passkey rule.
		const is_members = response.site.portal === `members` || response.site.portal === `accounts`;
		const member_or_user = is_members ? response.member : response.user;

		if( response.site.environment === `local` )	{
			return false;
		}

		if( !member_or_user?.uuid ) {
			return false;
		}

		if( !member_or_user.mfa_required ) {
			return false;
		}

		if( member_or_user.otp_factor.uuid !== `` ) {
			return false;
		}

		// Only a passkey this host can use counts (#2359), mirroring
		// mfa.MemberSatisfiesMFA: an older app-only passkey (rp_id
		// decisioninsights.ai, say) cannot be presented on a wiretaplabs.com
		// host, the server's gate no longer counts it there, and a client
		// that still did would leave the member on a page where every request
		// is refused instead of on the one where they can add a factor. The
		// test is WebAuthn's own: the RP ID must be this host or a registrable
		// suffix of it. No recorded RP counts, as it always has.
		if( is_members && member_or_user.passkey_factors?.some( passkey_usable_here ) ) {
			return false;
		}

		return true;
	}

	function passkey_usable_here( passkey ) {
		const rp_id = passkey.rp_id;

		if( !rp_id ) {
			return true;
		}

		const host = location.hostname;

		return host === rp_id || host.endsWith( `.${rp_id}` );
	}

	// Both consent gates below are the brand portal's, for the portal's own
	// brand. The accounts host (#2358) is not one app, and its site row is
	// always Wiretap Labs, so it does not gate on them: each app's Service
	// consent is enforced at /oauth2/authorize for that app, whose refusal
	// hands the app an error_uri to this host's consent page carrying the
	// pending authorization as redirect_to (oauth2/service_consent.go).
	function needs_service_consent() {
		if( response.site.portal !== `members` ) {
			return false;
		}

		if( !response.member?.uuid && !response?.is_partial ) {
			return false;
		}

		if( response.service_consent.uuid === `` ) {
			return false;
		}

		if( response.member_service_consent.uuid !== `` && response.member_service_consent.version >= response.service_consent.version ) {
			return false;
		}

		return true;
	}

	// Conditioning consent is captured at registration for anyone who signs
	// themselves up, but a member an admin creates on their behalf
	// (members/admin_api.go's adminAPIAddHandler) has no visitor present to
	// ask, so nothing is recorded and nothing ever asks later. This is that
	// later: same shape as needs_service_consent above, first login instead
	// of registration.
	function needs_conditioning_consent() {
		if( response.site.portal !== `members` ) {
			return false;
		}

		if( !response.member?.uuid && !response?.is_partial ) {
			return false;
		}

		if( response.conditioning_consent.uuid === `` ) {
			return false;
		}

		// Only gate where a missing member_consents row provably means "never
		// asked". Registration always writes a row for `default` (auto-saved)
		// and `required` (it cannot complete otherwise), so for those two a
		// missing row can only be an admin-created member. An `optional`
		// consent records NOTHING when a member declines it, which is
		// indistinguishable from never having been asked -- gating on it
		// would bounce a self-registered member who already answered back to
		// this page on every single login, with no way out.
		if( response.conditioning_consent.optionality === `optional` ) {
			return false;
		}

		if( response.member_conditioning_consent.uuid !== `` && response.member_conditioning_consent.version >= response.conditioning_consent.version ) {
			return false;
		}

		return true;
	}

	router.afterEach(
		( to, from ) => {
			if( !from.name ) {
				return;
			}

			nextTick( () => {
				window.wtlSensor?.pageview();
			} );
		}
	);

	router.beforeEach(
		to => {
			const store = useMainStore();

			// The brand portal's own sign-in pages, once its brand is cut over
			// to central sign-in. An emailed sign-in link from before the
			// cutover (dashboard with a token) still signs in here.
			if( central_sign_in_active() && [`dashboard`, `otp`, `register`, `forgot`].includes( to.name ) && !( to.name === `dashboard` && to.query.token ) ) {
				go_to_central_sign_in( to.name === `register` || to.name === `forgot` ? to.name : `login`, `/` );

				return false;
			}

			if( to.meta.title ) {
				document.title = `${to.meta.title} - ${store.name}`;
			} else {
				document.title = store.name;
			}

			if( to.name !== `security` && needs_mfa() ) {
				notify( `error`, `You must setup two-factor authentication before you can use the full functionality of your account` );

				return {
					name: `security`
				};
			}

			if( to.name !== `consent` && needs_service_consent() ) {
				return {
					name: `consent`
				};
			}

			// MFA first, then Service consent, then this -- one prompt at a
			// time, and both interlocks are load-bearing.
			//
			// Without !needs_service_consent() this fires while the member is
			// sitting on the `consent` route: that route clears the Service
			// check above by name, falls through to here, and gets redirected
			// off the Service prompt it was just sent to.
			//
			// Without !needs_mfa() it fires on the `security` route the MFA
			// check redirects to, bouncing the member back and forth between
			// the two forever. It would be a dead end even if it settled --
			// membersAPIConditioningConsentHandler authenticates with
			// VerifySessionWithMFA, so submitting this form before MFA is set
			// up can only 401.
			if( to.name !== `conditioning-consent` && !needs_mfa() && !needs_service_consent() && needs_conditioning_consent() ) {
				return {
					name: `conditioning-consent`
				};
			}
		}
	);
}
